import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Upload, History, Database, RefreshCw, Folder, Sparkles } from 'lucide-react';
import { useOrganizationContext } from '../organization.context';
import { UploadInterface } from '../components/auto-uploads/upload-interface';
import { UploadHistory } from '../components/auto-uploads/upload-history';
import { OrgMetadataExtractionDialog } from '../components/org-metadata-extraction-dialog';
import { Loader } from '@/components/loader';
import workspacesAPI from '@/api/workspaces.api';
import knowledgeCategoriesAPI from '@/api/knowledge-categories.api';
import knowledgeAPI from '@/api/knowledge.api';
import autoUploadsAPI from '@/api/auto-uploads.api';
import { useToast } from '@/components/ui/use-toast';
import { formatNumber } from 'shared/utils';

export default function KnowledgeManagementPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organization, loading, setOrganizationId } = useOrganizationContext();
  const { toast } = useToast();
  const activeTab = searchParams.get('tab') || 'overview';
  const [workspaces, setWorkspaces] = useState([]);
  const [stats, setStats] = useState({
    totalKnowledgeItems: 0,
    totalCategories: 0,
    recentUploads: 0,
    pendingProcessing: 0,
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [showMetadataDialog, setShowMetadataDialog] = useState(false);

  useEffect(() => {
    setOrganizationId(id);
  }, [id, setOrganizationId]);
  useEffect(() => {
    if (organization?.id) {
      fetchOrganizationData();
    }
  }, [organization?.id]);

  const fetchOrganizationData = async () => {
    setDataLoading(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const [
        workspacesResponse,
        categoriesResponse,
        knowledgeResponse,
        recentUploadsResponse,
        pendingUploadsResponse,
      ] = await Promise.all([
        workspacesAPI.getWorkspaces(),
        knowledgeCategoriesAPI.getCategories(),
        knowledgeAPI.getKnowledgeItemsCount(),
        autoUploadsAPI.getAllUploads({
          limit: 100, // Get more to count recent ones
          page: 1,
        }),
        autoUploadsAPI.getAllUploads({
          status: 'processing',
          limit: 100,
          page: 1,
        }),
      ]);

      if (workspacesResponse.error) {
        console.error('Error fetching workspaces:', workspacesResponse.error);
        toast({
          title: 'Error loading workspaces',
          description: 'Failed to load workspaces. Some features may not work properly.',
          variant: 'destructive',
        });
      } else {
        setWorkspaces(workspacesResponse.data || []);
      }

      if (categoriesResponse.error) {
        console.error('Error fetching categories:', categoriesResponse.error);
        toast({
          title: 'Error loading categories',
          description: 'Failed to load knowledge categories. Some features may not work properly.',
          variant: 'destructive',
        });
      }

      let totalKnowledgeItems = 0;
      if (knowledgeResponse.error) {
        console.error('Error fetching knowledge items:', knowledgeResponse.error);
      } else {
        totalKnowledgeItems = knowledgeResponse.data?.total ?? 0;
      }

      let recentUploadsCount = 0;
      if (recentUploadsResponse.error) {
        console.error('Error fetching recent uploads:', recentUploadsResponse.error);
      } else {
        const uploads = recentUploadsResponse.data?.uploads || [];
        recentUploadsCount = uploads.filter(upload => {
          const uploadDate = new Date(upload.createdAt);
          return uploadDate >= thirtyDaysAgo;
        }).length;
      }

      let pendingProcessingCount = 0;
      if (pendingUploadsResponse.error) {
        console.error('Error fetching pending uploads:', pendingUploadsResponse.error);
      } else {
        pendingProcessingCount = pendingUploadsResponse.data?.uploads?.length || 0;
      }

      setStats({
        totalKnowledgeItems,
        totalCategories: categoriesResponse.data?.length || 0,
        recentUploads: recentUploadsCount,
        pendingProcessing: pendingProcessingCount,
      });
    } catch (error) {
      console.error('Error fetching organization data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load organization data. Please try refreshing the page.',
        variant: 'destructive',
      });
    } finally {
      setDataLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Knowledge Management</h1>
        <p className="text-muted-foreground">
          Manage bulk document uploads and knowledge content for {organization?.name}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={tab => navigate(`?tab=${tab}`)} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Bulk Upload
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Upload History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Organization Overview</h2>
              <p className="text-sm text-muted-foreground">Key metrics and statistics</p>
            </div>
            <Button
              variant="default"
              onClick={fetchOrganizationData}
              disabled={dataLoading}
              className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Knowledge Items</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(stats.totalKnowledgeItems)}</div>
                <p className="text-xs text-muted-foreground">Total documents in system</p>
                <Button
                  size="sm"
                  onClick={() => setShowMetadataDialog(true)}
                  className="w-full mt-3">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Extract Metadata for All Items
                </Button>
              </CardContent>
            </Card>
            <OrgMetadataExtractionDialog
              open={showMetadataDialog}
              onClose={() => setShowMetadataDialog(false)}
            />

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Categories</CardTitle>
                <Folder className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalCategories}</div>
                <p className="text-xs text-muted-foreground">Knowledge categories</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Recent Uploads</CardTitle>
                <Upload className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.recentUploads}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Processing</CardTitle>
                <History className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.pendingProcessing}</div>
                <p className="text-xs text-muted-foreground">Currently processing</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Available Workspaces</CardTitle>
                <CardDescription>Workspaces where documents can be uploaded</CardDescription>
              </CardHeader>
              <CardContent>
                {dataLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-2 border rounded animate-pulse">
                        <div className="h-4 bg-muted rounded w-32"></div>
                        <div className="h-5 bg-muted rounded w-12"></div>
                      </div>
                    ))}
                  </div>
                ) : workspaces.length > 0 ? (
                  <div className="space-y-2">
                    {workspaces.map(workspace => (
                      <div
                        key={workspace.id}
                        className="flex items-center justify-between p-2 border rounded">
                        <span className="font-medium">{workspace.name}</span>
                        <Badge variant="outline">Active</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <p>No workspaces found</p>
                    <p className="text-xs mt-1">Contact your administrator to set up workspaces</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Auto Upload Features</CardTitle>
                <CardDescription>What happens when you upload documents</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">ZIP Extraction</p>
                      <p className="text-muted-foreground">
                        Automatically extracts all files from ZIP archives
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Smart Organization</p>
                      <p className="text-muted-foreground">
                        Creates categories based on folder structure
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">AI Metadata Extraction</p>
                      <p className="text-muted-foreground">
                        Automatically extracts titles, keywords, and medical classifications
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Knowledge Integration</p>
                      <p className="text-muted-foreground">
                        Creates searchable knowledge items with embeddings
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="upload" className="space-y-6">
          <UploadInterface organizationId={id} workspaces={workspaces} />
        </TabsContent>

        <TabsContent value="history">
          <UploadHistory organizationId={id} workspaces={workspaces} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
